import { Spinner, Typography } from '@weareopenr/component-library';
import CollapsibleSection from 'src/components/CollapsibleSection';
import { groupAndFormatSchedules } from 'src/pages/Mealtimes/groupAndFormatSchedules/groupAndFormatSchedules';
import { ISummaryViewProps } from './types';
import { IMealtime } from 'src/types/mealtimes.types';
import styles from './SummaryView.module.scss';
import { usePriceScheme } from 'src/hooks/usePriceScheme';
import { formatDateTimeDayJs } from 'src/helpers/formatDateTime/formatDateTimeDayJs';
import ChannelsSummarySection from './ChannelsSummarySection/ChannelsSummarySection';
import VenuesSummarySection from './VenuesSummarySection/VenuesSummarySection';
import PublishedTimeSummarySection from './PublishedTimeSummarySection/PublishedTimeSummarySection';

const SummaryView = ({
  menuName,
  watch,
  publicationData,
  filteredMealtimes,
  isMealtimeOptionEnabled = false,
  snapshotLabel = 'Menu snapshot',
}: ISummaryViewProps): React.ReactElement => {
  const { data: priceScheme, isLoading: isLoadingPriceScheme } = usePriceScheme(watch('price-scheme'));
  if (isLoadingPriceScheme) {
    return (
      <section className={styles['loading-container']}>
        <Spinner size="xlarge" colour="rainbow" />
      </section>
    );
  }

  const selectedChannelIds: string[] = watch('selectedChannels') ?? [];
  const selectedVenueIds: string[] = watch('venues') ?? [];

  const formValues = watch();
  const showMealtimes = filteredMealtimes !== undefined;
  // Sections are numbered in order, so they shift up when Mealtimes is hidden.
  const sectionNumber = (position: number) => String(showMealtimes ? position + 1 : position);

  return (
    <section>
      <div className={styles['heading-space']}>
        <Typography size="xxlarge" className={styles['heading']}>
          Summary for <strong>{menuName}</strong>
        </Typography>
        <Typography size="medium">
          {snapshotLabel} to be published -{' '}
          <strong>
            {menuName} ({formatDateTimeDayJs(new Date())})
          </strong>
        </Typography>
      </div>
      <div className={styles['sections']}>
        {showMealtimes && (
          <CollapsibleSection number="1" title="Mealtimes" subtitle={isMealtimeOptionEnabled ? 'Enabled' : 'Disabled'}>
            {isMealtimeOptionEnabled && !!filteredMealtimes.length && (
              <div className={styles['schedules']}>
                {filteredMealtimes.map((mealtime: IMealtime) => {
                  const schedules = groupAndFormatSchedules(mealtime.schedules);
                  return (
                    <div key={mealtime.name}>
                      <table>
                        <thead>
                          <tr>
                            <th className={styles['mealtime-name']}>
                              <Typography weight="semi-bold" transform="capitalize">
                                {mealtime.name}:
                              </Typography>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {schedules.map((schedule) => (
                            <tr key={`${schedule.startDay}-${schedule.endDay}-${schedule.time}`}>
                              <td className={styles['day']}>
                                <Typography>
                                  {`${schedule.startDay}${schedule.endDay ? ' - ' + schedule.endDay : ''}:`}
                                </Typography>
                              </td>
                              <td>
                                <Typography>{schedule.time}</Typography>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </CollapsibleSection>
        )}
        <CollapsibleSection number={sectionNumber(1)} title="Pricing" subtitle={priceScheme?.name} />
        <ChannelsSummarySection
          number={sectionNumber(2)}
          publicationData={publicationData}
          selectedChannelIds={selectedChannelIds}
        />
        <VenuesSummarySection
          number={sectionNumber(3)}
          publicationData={publicationData}
          selectedChannelIds={selectedChannelIds}
          selectedVenueIds={selectedVenueIds}
        />
        <PublishedTimeSummarySection
          number={sectionNumber(4)}
          publicationDate={formValues.publicationDate}
          publicationType={formValues.publicationType}
        />
      </div>
    </section>
  );
};

export default SummaryView;
